/**
 * Domain binding — fonte única de verdade.
 *
 * O mesmo matcher roda em três lugares, de propósito:
 *  - backend (`getConfig`): barreira real, decide entre entregar a config ou responder 403;
 *  - SDK: fail-fast de UX antes de renderizar (client-side é burlável, não é garantia);
 *  - painel: valida o que o usuário digita com exatamente a mesma regra do servidor.
 *
 * Regras de uma entrada da allowlist:
 *  - `cliente.com`      → casa só com `cliente.com` (o ápice, nada de subdomínio);
 *  - `*.cliente.com`    → casa com **um** nível de subdomínio (`www.cliente.com`, não `a.b.cliente.com`),
 *                         e **não** casa com o ápice — quem quiser os dois lista os dois;
 *  - `localhost`, `127.0.0.1` → permitidos, para desenvolvimento;
 *  - `*` sozinho, vazio ou curinga fora da primeira label → inválidos.
 *
 * Nomes são comparados em minúsculas e sem o ponto final de FQDN — `Origin: http://CLIENTE.com.`
 * não deve escapar da lista.
 */
const LABEL = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
/** Tira esquema, porta, caminho, credenciais e ponto final. Devolve `null` se não sobrar host. */
export function normalizeHostname(value) {
    if (!value)
        return null;
    let host = String(value).trim().toLowerCase();
    if (!host)
        return null;
    // Aceita URL completa, origem, ou host cru.
    if (host.includes('://')) {
        try {
            host = new URL(host).hostname;
        }
        catch {
            return null;
        }
    }
    else {
        host = host.split('/')[0] ?? '';
        // IPv6 entre colchetes: [::1]:8080
        if (host.startsWith('[')) {
            const end = host.indexOf(']');
            if (end === -1)
                return null;
            host = host.slice(1, end);
        }
        else if (host.includes('@')) {
            host = host.slice(host.lastIndexOf('@') + 1);
            host = host.split(':')[0] ?? '';
        }
        else {
            host = host.split(':')[0] ?? '';
        }
    }
    host = host.replace(/\.+$/, '');
    return host || null;
}
/** Normaliza uma entrada da allowlist, preservando o curinga `*.`. */
export function normalizeDomainEntry(entry) {
    if (!entry)
        return null;
    const raw = String(entry).trim().toLowerCase();
    if (!raw)
        return null;
    if (raw.startsWith('*.')) {
        const rest = normalizeHostname(raw.slice(2));
        return rest ? `*.${rest}` : null;
    }
    return normalizeHostname(raw);
}
function isIpv4(host) {
    const parts = host.split('.');
    if (parts.length !== 4)
        return false;
    return parts.every((p) => /^\d{1,3}$/.test(p) && Number(p) <= 255);
}
function isPlainHostValid(host) {
    if (host.length > 253)
        return false;
    if (isIpv4(host))
        return true;
    if (host.includes(':'))
        return /^[0-9a-f:]+$/.test(host); // IPv6 literal (ex.: ::1)
    const labels = host.split('.');
    return labels.every((l) => l.length > 0 && l.length <= 63 && LABEL.test(l));
}
/**
 * Uma entrada é válida? Recusa `*` solto e curinga que não seja a primeira label inteira
 * (`*evil.com`, `a.*.com`) — são as formas que transformariam a allowlist em decoração.
 */
export function isValidDomainEntry(entry) {
    const normalized = normalizeDomainEntry(entry);
    if (!normalized)
        return false;
    if (normalized === '*' || normalized === '*.')
        return false;
    if (normalized.startsWith('*.')) {
        const base = normalized.slice(2);
        if (!base || base.includes('*'))
            return false;
        // Curinga sobre TLD (`*.com`) é praticamente `*`: recusado.
        if (!base.includes('.'))
            return false;
        return isPlainHostValid(base);
    }
    if (normalized.includes('*'))
        return false;
    return isPlainHostValid(normalized);
}
/** O hostname está autorizado por esta allowlist? */
export function matchesDomain(hostname, authorizedDomains) {
    const host = normalizeHostname(hostname);
    if (!host || !authorizedDomains || authorizedDomains.length === 0)
        return false;
    for (const raw of authorizedDomains) {
        const entry = normalizeDomainEntry(raw);
        if (!entry || !isValidDomainEntry(entry))
            continue;
        if (entry.startsWith('*.')) {
            const base = entry.slice(2);
            if (host === base)
                continue; // curinga não cobre o ápice
            if (!host.endsWith(`.${base}`))
                continue;
            const prefix = host.slice(0, host.length - base.length - 1);
            // Um único nível de subdomínio.
            if (prefix.length > 0 && !prefix.includes('.'))
                return true;
            continue;
        }
        if (host === entry)
            return true;
    }
    return false;
}
/**
 * Extrai o hostname que deve ser checado a partir dos cabeçalhos da requisição.
 * `Origin` é a fonte preferida; `Referer` é o fallback (algumas navegações não mandam Origin
 * em GET). Sem nenhum dos dois, devolve `null` — e quem chama trata isso como bloqueio.
 */
export function hostnameFromHeaders(headers) {
    return normalizeHostname(headers.origin) ?? normalizeHostname(headers.referer);
}
//# sourceMappingURL=domain.js.map